import type { HTMLAttributes, Key } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Chip, Tooltip } from '@mui/material';
import tid from '@/common/application/utils/tid';
import { CrewAssignmentStatus } from '@/common/infrastructure/gql/generated/schema';
import { CertificationWarningIcon } from '@/raptorjob/presentation/components/RaptorJobDetails/components/JobTicketOverview/JobTicketOverviewERP/components/CertificationWarningIcon';
import { STATUS_COLORS } from '@/common/presentation/constants/colors';
import type { CrewEmployeeOption } from './useCrewRowEmployeeAutocompleteState';

interface EmployeeResource {
  id: number;
}

interface CrewEmployeeAutocompleteListItemProps {
  listProps: HTMLAttributes<HTMLLIElement> & { key?: Key };
  option: CrewEmployeeOption;
  roleId: number | null;
  isEmployeeCompliant?: (employee: EmployeeResource, roleId: number) => boolean;
  getEmployeeMissingCerts?: (employee: EmployeeResource, roleId: number) => string[];
}

export function CrewEmployeeAutocompleteListItem({
  listProps,
  option,
  roleId,
  isEmployeeCompliant,
  getEmployeeMissingCerts
}: CrewEmployeeAutocompleteListItemProps) {
  const isBanned = option.status?.includes(CrewAssignmentStatus.Banned);
  const isOutOfOffice = option.status?.includes(CrewAssignmentStatus.OutOfOffice);
  const employeeResource = { id: option.id };
  const isCompliant = roleId && isEmployeeCompliant ? isEmployeeCompliant(employeeResource, roleId) : true;
  const missingCerts = roleId && getEmployeeMissingCerts && !isCompliant ? getEmployeeMissingCerts(employeeResource, roleId) : [];

  return (
    <Box component="li" {...listProps} key={option.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {isEmployeeCompliant !== undefined &&
        (isCompliant ? (
          <Tooltip title="Meets all certification requirements" placement="top">
            <CheckCircleIcon
              sx={{ fontSize: 16, color: STATUS_COLORS.SUCCESS_MAIN, flexShrink: 0 }}
              {...tid(`jobs:ticket-details:edit-resources-modal:crew-compliance-icon-${option.id}`)}
            />
          </Tooltip>
        ) : (
          <CertificationWarningIcon
            tooltipText={`Missing certifications: ${missingCerts.join(', ')}`}
            testId={`jobs:ticket-details:edit-resources-modal:crew-compliance-icon-${option.id}`}
          />
        ))}
      <Box component="span">{option.name}</Box>
      {isBanned && (
        <Chip
          label="BANNED"
          size="small"
          sx={{
            backgroundColor: 'error.main',
            color: 'common.white',
            fontWeight: 600,
            fontSize: '0.75rem',
            height: '20px'
          }}
        />
      )}
      {isOutOfOffice && !isBanned && (
        <Chip
          label="OUT OF OFFICE"
          size="small"
          sx={{
            backgroundColor: 'warning.main',
            color: 'common.white',
            fontWeight: 600,
            fontSize: '0.75rem',
            height: '20px'
          }}
        />
      )}
    </Box>
  );
}
